import {
  Venue,
  CreateVenueDto,
  UpdateVenueDto,
  InviteUserDto,
  InviteUserResponse,
  GetVenueUsersParams,
  GetVenueUsersResponse,
  UpdateVenueUserDto,
} from "@/types";
import { axiosInstance } from "@/lib/axios";

export interface GetVenuesParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  organisationId?: string;
}

export interface GetVenuesResponse {
  data: Venue[];
  count: number;
}

/**
 * Get all venues with pagination
 */
export const getVenues = async (
  params?: GetVenuesParams,
): Promise<GetVenuesResponse> => {
  const response = await axiosInstance.get<GetVenuesResponse>("/venues", {
    params,
  });
  return response.data;
};

/**
 * Get a single venue by ID
 */
export const getVenueById = async (id: string): Promise<Venue> => {
  const response = await axiosInstance.get<Venue>(`/venues/${id}/info`);
  return response.data;
};

/**
 * Create a new venue
 */
export const createVenue = async (data: CreateVenueDto): Promise<Venue> => {
  const response = await axiosInstance.post<Venue>("/venues", data);
  return response.data;
};

/**
 * Update an existing venue
 */
export const updateVenue = async (
  id: string,
  data: UpdateVenueDto,
): Promise<Venue> => {
  const response = await axiosInstance.patch<Venue>(`/venues/${id}`, data);
  return response.data;
};

/**
 * Delete a venue
 */
export const deleteVenue = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/venues/${id}`);
};

export interface CheckSlugResponse {
  exists: boolean;
}

/**
 * Check whether a venue slug already exists
 */
export const checkSlug = async (slug: string): Promise<CheckSlugResponse> => {
  const response = await axiosInstance.get<CheckSlugResponse>(
    "/venues/check-slug",
    {
      params: { slug },
    },
  );
  return response.data;
};

/**
 * Get users for a specific venue with pagination
 */
export const getVenueUsers = async (
  venueId: string,
  params?: GetVenueUsersParams,
): Promise<GetVenueUsersResponse> => {
  const response = await axiosInstance.get<GetVenueUsersResponse>(
    `/venues/${venueId}/users`,
    { params },
  );
  return response.data;
};

/**
 * Invite users to a venue (supports multiple invitations)
 * API: POST /venues/{venueId}/users/invite
 * @see https://dev.api.venue.relivable.com/api/docs#/Venues/VenueController_inviteUser
 */
export const inviteVenueUser = async (
  venueId: string,
  data: InviteUserDto,
): Promise<InviteUserResponse> => {
  const response = await axiosInstance.post<InviteUserResponse>(
    `/venues/${venueId}/users/invite`,
    data,
  );
  return response.data;
};

/**
 * Update a venue user's role and organizational roles
 * API: PATCH /venues/{venueId}/users/{userId}
 */
export const updateVenueUser = async (
  venueId: string,
  userId: string,
  data: UpdateVenueUserDto,
): Promise<void> => {
  await axiosInstance.patch(`/venues/${venueId}/users/${userId}`, data);
};

/**
 * Remove a user from a venue
 * API: DELETE /venues/{venueId}/users/{userId}
 */
export const deleteVenueUser = async (
  venueId: string,
  userId: string,
): Promise<void> => {
  await axiosInstance.delete(`/venues/${venueId}/users/${userId}`);
};

/**
 * Re-invite a previously invited user to a venue
 * API: POST /venues/{venueId}/users/{userId}/reinvite
 */
export const reinviteVenueUser = async (
  venueId: string,
  userId: string,
): Promise<void> => {
  await axiosInstance.post(`/venues/${venueId}/users/${userId}/reinvite`);
};
